// Заголовки, которые глобальный axios добавляет к запросу: X-Client-Token и
// X-App-Version (anti-scraping) и восстановленный из localStorage Authorization.
//
// Вынесено из main.js фабрикой, потому что main.js не импортируется ни одним
// спеком (он поднимает Vue, роутер, стор и Vuetify) — а поведение этих трёх
// заголовков решает, уходит ли наш JWT на чужой хост.
//
// Ни один из трёх не уходит за пределы нашего API: JWT живёт 30 дней
// (rentalman-api config/initializers/devise.rb:20) и даёт полный доступ от лица
// пользователя, client_token снимает анти-скрейпинг, а X-App-Version хоть и не
// секрет, но любой кастомный заголовок делает кросс-доменный запрос
// preflight'нутым — оставлять его в отрыве от остальных смысла нет.
//
// На чужом адресате мало НЕ ДОБАВИТЬ заголовки — их надо СНЯТЬ. У залогиненного
// пользователя токен лежит в `axios.defaults.headers.common.Authorization`
// (store.js:329, session_manager.js:98), а axios мержит defaults в config ещё до
// интерсепторов: к моменту нашей проверки заголовок уже проставлен, и ранний
// `return config` отправил бы его третьей стороне. Именно этот путь — основной
// для залогиненного пользователя, то есть почти для всех.

import { isOurApiRequest } from "@/utils/apiOrigin";

function readHeader(config, name) {
  const headers = config.headers;
  if (!headers) return undefined;
  return typeof headers.get === "function" ? headers.get(name) : headers[name];
}

function deleteHeader(config, name) {
  const headers = config.headers;
  if (!headers) return;
  if (typeof headers.delete === "function") {
    headers.delete(name);
    return;
  }
  // Plain-object конфиг (тесты, ручная сборка config): AxiosHeaders нормализует
  // регистр сам, а голый объект — нет, поэтому чистим оба написания.
  delete headers[name];
  delete headers[name.toLowerCase()];
}

// Снять с чужого запроса всё, что принадлежит нам.
//
// X-Client-Token и X-App-Version наши всегда — уходят безусловно. Authorization
// снимается только если его значение совпало с нашим собственным токеном:
// чужой ключ, поставленный самим вызовом (Basic для Traccar в AppHeader.vue:1640,
// ключ стороннего сервиса), обязан дойти до адресата нетронутым.
function stripOwnHeaders(config, ctx) {
  deleteHeader(config, 'X-Client-Token');
  deleteHeader(config, 'X-App-Version');

  const current = readHeader(config, 'Authorization');
  if (!current) return;

  const ours = (ctx.getOwnAuthTokens ? ctx.getOwnAuthTokens() : []) || [];
  if (ours.indexOf(current) !== -1) {
    deleteHeader(config, 'Authorization');
  }
}

export function createRequestHeadersInterceptor(ctx) {
  return function requestHeadersInterceptor(config) {
    if (
      !isOurApiRequest(config, {
        bases: ctx.getOurApiBases(),
        pageOrigin: ctx.getPageOrigin(),
      })
    ) {
      stripOwnHeaders(config, ctx);
      return config;
    }

    // client_token ставит утилита анти-скрейпинга: у неё своя метка, без которой
    // повтор после рекавери затирает свежий токен значением из localStorage.
    ctx.applyClientToken(config);
    config.headers['X-App-Version'] = ctx.getAppVersion();
    // Восстановление auth-токена после refresh: vuex-persist восстанавливает state, но НЕ
    // вызывает мутацию, которая ставит axios.defaults.Authorization. Без этого первый запрос
    // после F5 уходит без токена → 401 → main.js 401-handler делает unsetCurrentUser → вышибает.
    // localStorage "auth_token" хранит "Bearer …" и переживает refresh.
    // Заголовок может прийти как null или строкой "null"/"Bearer null" (getAuthToken вернул
    // null в окне до гидрации) — трактуем все три как «токена нет» и подставляем из localStorage.
    var currentAuth = config.headers['Authorization'];
    if (!currentAuth || currentAuth === 'null' || currentAuth === 'Bearer null') {
      var authToken = ctx.getStoredAuthToken();
      if (authToken && authToken !== 'null') {
        config.headers['Authorization'] = authToken;
      }
    }
    return config;
  };
}
